import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AttributeValueModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    maximumNumberAttributeTypes: number;
    extendedStyles?: ProductAttributesStyles;
    hasAddToCart?: boolean;
}
const mapStateToProps = (state: ApplicationState) => ({
    danaGeneralSettings: getDanaGeneralSettings(state),
});
type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProduct;

export interface ProductAttributesStyles {
    container?: InjectableCss;
    attributeTypeGridItem?: InjectableCss;
    attributeValueGridItem?: InjectableCss;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueText?: TypographyPresentationProps;
    specAccordionSection?: AccordionSectionPresentationProps;
}

export const productAttributesStyles: ProductAttributesStyles = {
    container: {
        css: css``,
    },
    attributeTypeGridItem: {
        css: css`
            overflow: hidden;
        `,
    },
    attributeValueGridItem: {
        css: css`
            overflow: hidden;
        `,
    },
    attributeTypeText: {
        weight: "bold",
    },
};

const ProductAttributes: FC<Props> = ({
    product: { attributeTypes },
    maximumNumberAttributeTypes,
    extendedStyles,
    hasAddToCart,
    danaGeneralSettings
}) => {
    const [styles] = React.useState(() => mergeToNew(productAttributesStyles, extendedStyles));

    if (!attributeTypes || attributeTypes.length === 0) {
        return null;
    }
    const skipAttributes = danaGeneralSettings?.skipAttributeListName?.toLowerCase().split(",").map((s: any) => s.trim());
    const maximum = Math.min(maximumNumberAttributeTypes, 100);
    const limitedAttributeTypes = attributeTypes.slice(0, maximum);

    const displayAttributeValues = (attributeValues: AttributeValueModel[] | null) => {
        return (
            <Typography {...styles.attributeValueText}>
                {(attributeValues ?? []).map(a => a.valueDisplay).join(", ")}
            </Typography>
        );
    };

    return (
        <StyledWrapper {...styles.container} className={hasAddToCart ? "with-add-to-cart" : ""}>
            {limitedAttributeTypes?.length >= 0 && (
                <ManagedAccordionSection
                    title="Specifications"
                    initialExpanded={false}
                    {...styles.specAccordionSection}
                >
                    {limitedAttributeTypes.map((attributeType) =>
                        !skipAttributes?.includes(attributeType.label?.toLowerCase()) &&
                        attributeType.attributeValues && (
                            <React.Fragment key={attributeType.id.toString()}>
                                <StyledWrapper {...styles.attributeTypeGridItem}>
                                    <Typography
                                        {...styles.attributeTypeText}
                                        data-test-selector={`attributeType${attributeType.id}`}
                                        className="text-body-sm"
                                    >
                                        {attributeType.label || attributeType.name}
                                    </Typography>
                                </StyledWrapper>

                                <StyledWrapper
                                    {...styles.attributeValueGridItem}
                                    data-test-selector={`attributeValuesFor${attributeType.id}`}
                                    className="text-body-sm"
                                >
                                    {displayAttributeValues(attributeType.attributeValues)}
                                </StyledWrapper>
                            </React.Fragment>
                        )
                    )}
                </ManagedAccordionSection>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProduct(ProductAttributes));
